import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Stack,
    Text,
    SimpleGrid,
    InputGroup,
    useToast,
    InputLeftElement,
    Flex,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import { updateProfile, updatePassword } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebaseService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from "react-i18next";
import AmazonIntegration from '../components/Settings/AmazonIntegration';
import AmazonInventory from '../components/Amazon/AmazonInventory';

const Account = () => {
    
    const { currentUser, logout } = useAuth();
    const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
    const [photoURL] = useState(currentUser?.photoURL || '');
    const [email] = useState(currentUser?.email || '');
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [credits, setCredits] = useState(0);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [company, setCompany] = useState('');
    const [billingAddress, setBillingAddress] = useState('');
    const chakraToast = useToast();
    const { t } = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const defaultIndex = tabParam === '1' ? 1 : 0;

    useEffect(() => {
        if (currentUser) {
            const docRef = doc(db, 'users', currentUser.uid);
            getDoc(docRef).then(docSnap => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFirstName(data.firstName || '');
                    setLastName(data.lastName || '');
                    setCompany(data.company || '');
                    setBillingAddress(data.billingAddress || '');
                    setCredits(data.credits || 0);
                }
            });
        }
    }, [currentUser]);

    const handleSaveChanges = async () => {
        try {
            if (currentUser) {
                await updateProfile(currentUser, {
                    displayName,
                    photoURL,
                });

                const docRef = doc(db, 'users', currentUser.uid);
                await updateDoc(docRef, {
                    firstName,
                    lastName,
                    company,
                    billingAddress,
                });

                chakraToast({
                    title: t("changesSaved"),
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            chakraToast({
                title: t("failedToUpdateProfile"),
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            console.error('Failed to update profile', error);
        }
    };

    const validatePassword = (password: string) => {
        if (password.length < 6) {
            return t('passwordLengthError');
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return t('passwordSpecialCharError');
        }
        if (!/\d/.test(password)) {
            return t('passwordNumberError');
        }
        return null;
    };

    const handleChangePassword = async () => {
        const validationError = validatePassword(password);
        if (validationError) {
            chakraToast({
                title: validationError,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (password !== repeatPassword) {
            chakraToast({
                title: t("passwordsDoNotMatch"),
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (currentUser && password) {
            try {
                await updatePassword(currentUser, password);
                chakraToast({
                    title: t("passwordUpdatedSuccessfully"),
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } catch (error) {
                chakraToast({
                    title: t("failedToUpdatePassword"),
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                console.error('Failed to update password', error);
            }
        }
    };

    return (
        <Box p={8} margin="0 auto">
            <Tabs defaultIndex={defaultIndex} variant="soft-rounded" colorScheme="primary" isLazy>
                <TabList mb="1em">
                    <Tab color="white" _selected={{ color: "white", bg: "primary.500" }}>{t('profil')}</Tab>
                    <Tab color="white" _selected={{ color: "white", bg: "primary.500" }}>Amazon SP-API</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel px={0}>
            <Stack spacing={6}>
                <Card>
                    <CardHeader>
                        <Text fontSize="xl" fontWeight="bold">{t('profil')}</Text>
                    </CardHeader>
                    <CardBody>
                        <SimpleGrid width='100%' columns={{ base: 1, md: 2 }} spacing='20px'>
                            <FormControl id="displayName">
                                <FormLabel>{t('nomDutilisateur')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="@" />
                                    <Input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="email">
                                <FormLabel>Email</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="@" />
                                    <Input
                                        type="email"
                                        value={email}
                                        isReadOnly
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="firstName">
                                <FormLabel>{t('prenom')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="👤" />
                                    <Input
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="lastName">
                                <FormLabel>{t('nom')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="👤" />
                                    <Input
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="company">
                                <FormLabel>{t('entreprise')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="🏢" />
                                    <Input
                                        type="text"
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="billingAddress">
                                <FormLabel>{t('adresseDeFacturation')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="🏠" />
                                    <Input
                                        type="text"
                                        value={billingAddress}
                                        onChange={(e) => setBillingAddress(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <Flex justifyContent="space-between" alignItems="center">
                                <Button colorScheme="primary" onClick={handleSaveChanges}>
                                    {t('sauvegarderLesModifications')}
                                </Button>
                            </Flex>
                        </SimpleGrid>
                    </CardBody>
                </Card>
                <Card>
                    <CardHeader>
                        <Text fontSize="xl" fontWeight="bold">{t('motDePasse')}</Text>
                    </CardHeader>
                    <CardBody>
                        <SimpleGrid width='100%' columns={{ base: 1, md: 2 }} spacing='20px'>
                            <FormControl id="password">
                                <FormLabel>{t('nouveauMotDePasse')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="🔒" />
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl id="repeatPassword">
                                <FormLabel>{t('repeterMotDePasse')}</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" children="🔒" />
                                    <Input
                                        type="password"
                                        value={repeatPassword}
                                        onChange={(e) => setRepeatPassword(e.target.value)}
                                    />
                                </InputGroup>
                            </FormControl>
                            <Button colorScheme="primary" onClick={handleChangePassword} width="full" mt={4}>
                                {t('changePasswordButton')}
                            </Button>
                        </SimpleGrid>
                    </CardBody>
                </Card>

                <Button colorScheme="red" variant="outline" onClick={logout} width="full">
                    {t('disconnectButton')}
                </Button>
            </Stack>
                    </TabPanel>

                    <TabPanel px={0}>
                        <Stack spacing={6}>
                            <AmazonIntegration />
                            <AmazonInventory />
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
            
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Box>
    );
};

export default Account;